import type { DocumentRecord } from '@/types/catalog'

/**
 * Resolving a catalog selection to full {@link DocumentRecord}s.
 *
 * The catalog list is server-filtered and paginated while the selection is
 * session-persisted, so the two drift apart: changing the Document Type filter
 * refetches and REPLACES the loaded rows, yet every id selected under the
 * previous filter stays selected. An action that resolves the selection with
 * `documents.filter((d) => selectedIds.has(d.id))` therefore sees only the part
 * of the selection that happens to be on the current page.
 *
 * FS-625: that is how "Open in new board" came to create a board holding only
 * the last file type selected — the button counted 5 files, the board got 2.
 * Ids the page no longer holds are resolved from the server instead
 * ({@link unloadedSelectedIds} + `batchQueryDocs`).
 *
 * The type-only counterpart is `resolveSelectedDocTypes` in
 * ./matchEntityEligibility, which answers the same question for
 * selection-gated *eligibility* (where a captured type is enough and a
 * round-trip would be wasteful).
 */

/**
 * The selected ids that `loadedDocs` cannot resolve, in selection order and
 * deduplicated — i.e. exactly what has to be hydrated from the server.
 * Returns `[]` when the whole selection is on the page, so the caller can skip
 * the round-trip entirely.
 */
export function unloadedSelectedIds(
  selectedIds: Iterable<string>,
  loadedDocs: ReadonlyArray<Pick<DocumentRecord, 'id'>>,
): string[] {
  const loaded = new Set(loadedDocs.map((doc) => doc.id))
  const missing: string[] = []
  const seen = new Set<string>()
  for (const id of selectedIds) {
    if (loaded.has(id) || seen.has(id)) continue
    seen.add(id)
    missing.push(id)
  }
  return missing
}

/**
 * Full records for a selection, in selection order.
 *
 * A loaded row wins over a hydrated one — it carries the live SSE status
 * updates the page has been applying. Ids neither source resolves (deleted from
 * another tab, say) are dropped: they can't be put on a board either way.
 */
export function resolveSelectedDocs(
  selectedIds: Iterable<string>,
  loadedDocs: ReadonlyArray<DocumentRecord>,
  hydratedDocs: ReadonlyArray<DocumentRecord> = [],
): DocumentRecord[] {
  const byId = new Map<string, DocumentRecord>()
  for (const doc of hydratedDocs) byId.set(doc.id, doc)
  for (const doc of loadedDocs) byId.set(doc.id, doc)

  const resolved: DocumentRecord[] = []
  const seen = new Set<string>()
  for (const id of selectedIds) {
    if (seen.has(id)) continue
    seen.add(id)
    const doc = byId.get(id)
    if (doc) resolved.push(doc)
  }
  return resolved
}
